import { Button } from "@/components/ui/button";
import { Calendar, FileText, DollarSign, Home, HelpCircle, X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import CanonicalTag from "@/components/CanonicalTag";
import { useState } from "react";

const HowToUseADUForLiveInCaregiver = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const portfolioImages = [
    "https://images.unsplash.com/photo-1721322800607-8c38375eef04?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop",
    "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?w=800&h=600&fit=crop"
  ];

  const ctaButtons = [
    {
      icon: Calendar,
      text: "Book & Schedule",
      onClick: () => setShowCalendly(true)
    },
    {
      icon: FileText,
      text: "What fits my lot?",
      onClick: () => window.location.href = '/landing'
    },
    {
      icon: DollarSign,
      text: "Get your price & ROI report",
      onClick: () => window.location.href = '/adu-calculator'
    },
    {
      icon: Home,
      text: "Our Portfolio",
      onClick: () => window.open("/our-work", "_blank")
    },
    {
      icon: HelpCircle,
      text: "Our FAQ",
      onClick: () => window.open("/faq", "_blank")
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-for-live-in-caregiver" />
      <Navigation />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold mb-8" style={{ color: '#E4683B' }}>
                How to Use Your ADU for a Live-In Caregiver
              </h1>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 justify-center mb-12">
                {ctaButtons.map((button, index) => (
                  <Button
                    key={index}
                    onClick={button.onClick}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    <button.icon className="w-4 h-4" />
                    {button.text}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#E4683B' }}>
                  Comfort for Your Family. Privacy for Their Caregiver.
                </h2>
                <p className="text-lg text-white mb-8">
                  If you or a loved one needs long-term assistance, an ADU offers the perfect solution for a live-in caregiver: close enough to help at a moment's notice, but private enough for both parties to feel at home.
                </p>
              </div>
              <div className="relative">
                <img
                  src={portfolioImages[0]}
                  alt="Small bedroom and private kitchen area for caregiver"
                  className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 bg-background/50">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <img
                  src={portfolioImages[1]}
                  alt="Full bathroom and quiet caregiver rest area"
                  className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#E4683B' }}>
                  Benefits
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: '#E4683B' }}></div>
                    <p className="text-white">Reliable caregiver presence</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: '#E4683B' }}></div>
                    <p className="text-white">Separate rest area with amenities</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: '#E4683B' }}></div>
                    <p className="text-white">Safe, quiet layout supporting work-life balance</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: '#E4683B' }}></div>
                    <p className="text-white">Ideal for in-home nurses, aides, or au pairs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative mb-12">
              <img
                src={portfolioImages[2]}
                alt="Professional caregiver accommodations"
                className="rounded-lg shadow-2xl w-full h-[300px] object-cover"
              />
            </div>
            <p className="text-xl text-white mb-12">
              Give your caregiver the dignity and space they deserve—while ensuring your family stays supported 24/7.
            </p>
            
            {/* Bottom CTA Buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              {ctaButtons.map((button, index) => (
                <Button
                  key={index}
                  onClick={button.onClick}
                  variant="secondary"
                  className="flex items-center gap-2"
                >
                  <button.icon className="w-4 h-4" />
                  {button.text}
                </Button>
              ))}
            </div>
          </div>
        </section>
      </main>

      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button 
              onClick={() => setShowCalendly(false)} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default HowToUseADUForLiveInCaregiver;